"use client";

import React, { useMemo, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import {
  ArrowLeft,
  Minus,
  Plus,
  ShieldCheck,
  ShoppingBag,
  Truck,
  Check,
} from "lucide-react";

import Navbar from "@/components/navbar";
import Footer from "@/components/footer";
import { SizeGuideModal } from "@/components/store/size-guide-modal";
import { useCart } from "@/context/cart-context";
import type { StoreProduct } from "@/components/store/types";

type ProductDetailProps = Readonly<{
  product: StoreProduct;
  collectionName: string;
  collectionHref: string;
  collectionProducts: readonly StoreProduct[];
}>;

const MAX_QUANTITY = 10;

// Decide whether a swatch color is light enough to need a dark check mark.
const isLightColor = (hex: string): boolean => {
  const normalized = hex.replace("#", "");

  if (normalized.length !== 6) {
    return false;
  }

  const r = parseInt(normalized.slice(0, 2), 16);
  const g = parseInt(normalized.slice(2, 4), 16);
  const b = parseInt(normalized.slice(4, 6), 16);

  // Perceived luminance (ITU-R BT.601).
  return (r * 299 + g * 587 + b * 114) / 1000 > 150;
};

export const ProductDetail = ({
  product,
  collectionName,
  collectionHref,
  collectionProducts,
}: ProductDetailProps): React.ReactElement => {
  const { addToCart } = useCart();

  // Variant State
  const firstAvailableVariant = useMemo(() => {
    return product.variants.find((v) => v.isAvailable) ?? product.variants[0];
  }, [product.variants]);

  const [selectedVariantId, setSelectedVariantId] = useState(
    firstAvailableVariant?.id ?? "",
  );
  const [quantity, setQuantity] = useState(1);

  const selectedVariant = useMemo(() => {
    return (
      product.variants.find((v) => v.id === selectedVariantId) ??
      firstAvailableVariant
    );
  }, [firstAvailableVariant, selectedVariantId, product.variants]);

  const selectedColor = selectedVariant?.colorName ?? product.colors[0];

  // Gallery shows the images for the selected color, falling back to the
  // product-level images when a color has none of its own.
  const galleryImages = useMemo(() => {
    const colorImages = selectedColor
      ? product.imagesByColor[selectedColor]
      : undefined;

    return colorImages && colorImages.length > 0 ? colorImages : product.images;
  }, [product.imagesByColor, product.images, selectedColor]);

  // Gallery Image State
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const activeImage = galleryImages[activeImageIndex] ?? galleryImages[0];

  const [isSizeGuideOpen, setIsSizeGuideOpen] = useState(false);

  const sizeVariants = useMemo(() => {
    return product.variants.filter((variant) => {
      return !selectedColor || variant.colorName === selectedColor;
    });
  }, [product.variants, selectedColor]);

  // If color changes, make sure the currently selected size variant belongs to the new color
  const handleColorChange = (colorName: string): void => {
    const matchingVariant =
      product.variants.find(
        (v) => v.colorName === colorName && v.isAvailable,
      ) ?? product.variants.find((v) => v.colorName === colorName);

    if (matchingVariant) {
      setSelectedVariantId(matchingVariant.id);
      setActiveImageIndex(0);
    }
  };

  const decrementQuantity = (): void => {
    setQuantity((current) => Math.max(1, current - 1));
  };

  const incrementQuantity = (): void => {
    setQuantity((current) => Math.min(MAX_QUANTITY, current + 1));
  };

  const canAddToCart =
    Boolean(selectedVariant?.isAvailable) &&
    !product.isSoldOut &&
    product.variants.length > 0;

  const handleAddToCart = (): void => {
    if (!selectedVariant || !canAddToCart) {
      return;
    }

    addToCart(product, { variant: selectedVariant, quantity });
  };

  const relatedProducts = useMemo(() => {
    const sameCategory = collectionProducts.filter(
      (p) => p.category === product.category && p.id !== product.id,
    );
    if (sameCategory.length > 0) {
      return sameCategory.slice(0, 3);
    }
    return collectionProducts.filter((p) => p.id !== product.id).slice(0, 3);
  }, [collectionProducts, product]);

  return (
    <>
      <Navbar variant="light" />

      <main className="text-foreground bg-white pt-24 pb-16 sm:pt-28 sm:pb-24">
        <div className="mx-auto max-w-[1312px] px-4 md:px-16">
          {/* Breadcrumbs / Back button */}
          <div className="flex items-center justify-between py-6">
            <Link
              href={collectionHref}
              className="font-sora text-secondary hover:text-primary flex items-center gap-2 text-sm font-semibold transition-colors"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
              Back to {collectionName}
            </Link>

            <nav
              className="text-secondary hidden items-center gap-2 text-sm font-semibold sm:flex"
              aria-label="Breadcrumb"
            >
              <Link
                href={collectionHref}
                className="hover:text-primary transition-colors"
              >
                Store
              </Link>
              <span aria-hidden="true">/</span>
              <span className="text-primary font-semibold">
                {collectionName}
              </span>
            </nav>
          </div>

          {/* Product Showcase */}
          <div className="grid min-w-0 gap-8 lg:grid-cols-[1.1fr_0.9fr] lg:items-start lg:gap-12">
            {/* Left: Gallery — thumbnail rail on the left, main image on the right */}
            <div className="flex min-w-0 flex-col-reverse gap-4 lg:flex-row">
              {/* Thumbnail rail */}
              {galleryImages.length > 1 && (
                <div className="flex max-w-full min-w-0 scrollbar-thin gap-3 overflow-x-auto pb-1 lg:max-h-[560px] lg:flex-col lg:overflow-x-visible lg:overflow-y-auto lg:pr-1 lg:pb-0">
                  {galleryImages.map((image, index) => (
                    <button
                      key={image.id}
                      type="button"
                      onClick={() => setActiveImageIndex(index)}
                      aria-label={`Show image ${index + 1} of ${galleryImages.length}`}
                      className={`bg-primary/5 relative aspect-square w-20 shrink-0 cursor-pointer overflow-hidden rounded-xl border transition-all ${
                        index === activeImageIndex
                          ? "border-primary ring-primary/20 ring-2"
                          : "border-border hover:border-primary/50"
                      }`}
                    >
                      <Image
                        src={image.transformedUrl ?? image.url}
                        alt={`${product.name} thumbnail ${index + 1}`}
                        fill
                        sizes="80px"
                        className="object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}

              {/* Main image */}
              <div className="border-border bg-primary/5 relative aspect-square w-full min-w-0 flex-1 overflow-hidden rounded-2xl border md:rounded-3xl">
                {activeImage ? (
                  <Image
                    src={activeImage.transformedUrl ?? activeImage.url}
                    alt={product.name}
                    fill
                    loading="eager"
                    sizes="(max-width: 1024px) 100vw, 50vw"
                    className="object-cover"
                  />
                ) : (
                  <div className="bg-primary/10 flex h-full w-full items-center justify-center">
                    <Image
                      src="/images/logos/logomark.png"
                      alt=""
                      width={120}
                      height={120}
                      className="opacity-80"
                    />
                  </div>
                )}
                {product.isSoldOut && (
                  <span className="font-sora bg-foreground/85 absolute top-6 left-6 rounded-full px-4 py-1.5 text-sm font-semibold text-white backdrop-blur-xs">
                    Sold out
                  </span>
                )}
              </div>
            </div>

            {/* Right: Info panel */}
            <div className="border-border flex min-w-0 flex-col gap-6 rounded-2xl border bg-white p-4 shadow-xs sm:p-6 md:rounded-3xl md:p-8 lg:p-10">
              {/* Badges and tags */}
              <div className="flex items-center gap-2">
                <span className="font-sora text-primary border-primary/20 bg-primary/5 rounded-full border px-3 py-1 text-xs font-semibold">
                  {product.category}
                </span>
                {product.isSoldOut && (
                  <span className="font-sora text-secondary border-border bg-primary/5 rounded-full border px-3 py-1 text-xs font-semibold">
                    Sold out
                  </span>
                )}
              </div>

              {/* Title & Price */}
              <div>
                <h1 className="font-sora text-foreground text-2xl leading-tight font-semibold sm:text-3xl md:text-4xl">
                  {product.name}
                </h1>

                {product.priceLabel && (
                  <p className="font-sora text-primary mt-4 text-2xl font-semibold sm:text-3xl">
                    {selectedVariant?.priceLabel ?? product.priceLabel}
                    {selectedVariant?.compareAtPriceLabel && (
                      <s className="text-secondary ml-2.5 text-lg font-medium sm:text-xl">
                        {selectedVariant.compareAtPriceLabel}
                      </s>
                    )}
                  </p>
                )}
              </div>

              {/* Description */}
              <div className="border-border text-secondary my-2 border-t border-b py-6 text-base leading-relaxed">
                <p>{product.description}</p>
              </div>

              {/* Custom selection options */}
              <div className="space-y-6">
                {/* Color option */}
                {product.colors.length > 0 && (
                  <div>
                    <h3 className="font-sora text-foreground mb-3 text-sm font-semibold">
                      Color:{" "}
                      <span className="text-secondary font-medium">
                        {selectedColor}
                      </span>
                    </h3>
                    <div className="flex flex-wrap items-center gap-2">
                      {product.colors.map((color) => {
                        const variant = product.variants.find(
                          (v) => v.colorName === color,
                        );
                        const isSelected = color === selectedColor;

                        return (
                          <button
                            key={color}
                            type="button"
                            onClick={() => handleColorChange(color)}
                            className={`text-foreground inline-flex min-h-11 max-w-full cursor-pointer items-center gap-2 rounded-full border bg-white px-4 text-sm font-semibold transition-all hover:scale-[1.02] ${
                              isSelected
                                ? "border-primary ring-primary/10 shadow-2xs ring-2"
                                : "border-border hover:border-primary/50"
                            }`}
                          >
                            <span
                              aria-hidden="true"
                              className="flex h-4 w-4 items-center justify-center rounded-full border border-black/10"
                              style={{
                                backgroundColor:
                                  variant?.swatchColor ?? "#d4d4d8",
                              }}
                            >
                              {isSelected && (
                                <Check
                                  className={`h-2.5 w-2.5 ${
                                    isLightColor(variant?.swatchColor ?? "")
                                      ? "text-black"
                                      : "text-white"
                                  }`}
                                />
                              )}
                            </span>
                            {color}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Size option */}
                <div>
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <h3 className="font-sora text-foreground text-sm font-semibold">
                      Size:{" "}
                      <span className="text-secondary font-medium">
                        {selectedVariant?.sizeName ?? "One size"}
                      </span>
                    </h3>
                    {product.sizeGuide && (
                      <button
                        type="button"
                        onClick={() => setIsSizeGuideOpen(true)}
                        className="font-sora text-primary cursor-pointer text-sm font-semibold underline-offset-2 hover:underline"
                      >
                        Size guide
                      </button>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {sizeVariants.map((variant) => {
                      const isSelected = variant.id === selectedVariant?.id;

                      return (
                        <button
                          key={variant.id}
                          type="button"
                          disabled={!variant.isAvailable}
                          onClick={() => setSelectedVariantId(variant.id)}
                          className={`font-sora disabled:hover:border-border inline-flex min-h-11 min-w-11 cursor-pointer items-center justify-center rounded-lg border bg-white px-4 text-sm font-semibold transition-all hover:scale-[1.02] disabled:cursor-not-allowed disabled:opacity-40 ${
                            isSelected
                              ? "border-primary text-primary ring-primary/10 ring-2"
                              : "border-border hover:border-primary/50 text-foreground"
                          }`}
                        >
                          {variant.sizeName ?? "One size"}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Quantity picker */}
                <div>
                  <h3 className="font-sora text-foreground mb-3 text-sm font-semibold">
                    Quantity
                  </h3>
                  <div className="border-border inline-flex min-h-11 items-center rounded-lg border bg-white shadow-2xs">
                    <button
                      type="button"
                      onClick={decrementQuantity}
                      disabled={quantity === 1}
                      className="hover:text-primary text-foreground flex h-11 w-11 cursor-pointer items-center justify-center transition-colors disabled:cursor-not-allowed disabled:opacity-40"
                      aria-label="Decrease quantity"
                    >
                      <Minus className="h-4 w-4" aria-hidden="true" />
                    </button>
                    <span className="font-sora text-foreground min-w-10 text-center text-sm font-semibold">
                      {quantity}
                    </span>
                    <button
                      type="button"
                      onClick={incrementQuantity}
                      disabled={quantity === MAX_QUANTITY}
                      className="hover:text-primary text-foreground flex h-11 w-11 cursor-pointer items-center justify-center transition-colors disabled:cursor-not-allowed disabled:opacity-40"
                      aria-label="Increase quantity"
                    >
                      <Plus className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Add to Cart CTA */}
              <div className="mt-4 pt-2">
                <button
                  type="button"
                  disabled={!canAddToCart}
                  onClick={handleAddToCart}
                  className={`font-sora inline-flex min-h-14 w-full cursor-pointer items-center justify-center gap-2 rounded-2xl px-6 py-4 text-base font-semibold shadow-md transition-all duration-200 hover:scale-[1.01] hover:shadow-lg ${
                    canAddToCart
                      ? "bg-primary hover:bg-primary/95 text-white"
                      : "bg-muted text-muted-foreground cursor-not-allowed shadow-none hover:scale-100"
                  }`}
                >
                  <ShoppingBag className="h-5 w-5" aria-hidden="true" />
                  {product.isSoldOut ? "Sold out" : "Add to Cart"}
                </button>
              </div>

              {/* Guarantees */}
              <div className="border-border mt-4 grid gap-3 border-t pt-6 sm:grid-cols-2">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="text-primary h-5 w-5 shrink-0" />
                  <span className="text-secondary text-xs font-semibold">
                    Official SimpleBLE merchandise
                  </span>
                </div>
                <Link
                  href="/returns"
                  className="text-secondary hover:text-primary text-xs font-semibold underline-offset-2 transition-colors hover:underline sm:col-span-2"
                >
                  Made to order · quality issues covered · returns policy
                </Link>
                <div className="flex items-center gap-2">
                  <Truck className="text-primary h-5 w-5 shrink-0" />
                  <span className="text-secondary text-xs font-semibold">
                    Printed on demand, ships worldwide
                  </span>
                </div>
              </div>

              {/* Expandable detail sections (More details, returns, etc.) */}
              {product.details.length > 0 && (
                <div className="border-border mt-2 border-t">
                  {product.details.map((section) => (
                    <details
                      key={section.title}
                      className="group border-border border-b"
                    >
                      <summary className="font-sora text-foreground flex cursor-pointer list-none items-center justify-between py-4 text-sm font-semibold [&::-webkit-details-marker]:hidden">
                        {section.title}
                        <Plus className="text-secondary h-4 w-4 shrink-0 group-open:hidden" />
                        <Minus className="text-secondary hidden h-4 w-4 shrink-0 group-open:block" />
                      </summary>
                      <ul className="text-secondary mt-1 mb-4 list-disc space-y-2 pl-5 text-sm leading-6">
                        {section.items.map((item, index) => (
                          <li key={index}>{item}</li>
                        ))}
                      </ul>
                    </details>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Related Products Section */}
          {relatedProducts.length > 0 && (
            <section className="border-border mt-16 border-t pt-10 sm:mt-24 sm:pt-12">
              <h2 className="font-sora text-foreground mb-8 text-center text-2xl md:text-left">
                More from {collectionName}
              </h2>

              <div className="grid min-w-0 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {relatedProducts.map((p) => (
                  <Link
                    key={p.id}
                    href={`/products/${p.slug}`}
                    className="group border-border hover:border-primary/30 flex h-full min-w-0 flex-col overflow-hidden rounded-2xl border bg-white transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
                  >
                    <div className="bg-primary/5 relative aspect-square overflow-hidden">
                      <Image
                        src={
                          p.images[0]?.transformedUrl ??
                          p.images[0]?.url ??
                          "/images/logos/logomark.png"
                        }
                        alt={p.name}
                        fill
                        sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                        className="object-cover transition-transform duration-300 group-hover:scale-[1.03]"
                      />
                      {p.isSoldOut && (
                        <span className="font-sora bg-foreground/80 absolute top-4 left-4 rounded-full px-3 py-1 text-xs font-semibold text-white">
                          Sold out
                        </span>
                      )}
                    </div>

                    <div className="flex flex-1 flex-col p-4 sm:p-5">
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
                        <h3 className="font-sora group-hover:text-primary text-foreground line-clamp-1 text-base font-semibold transition-colors">
                          {p.name}
                        </h3>
                        {p.priceLabel && (
                          <p className="font-sora text-primary text-sm font-semibold sm:shrink-0">
                            {p.priceLabel}
                          </p>
                        )}
                      </div>

                      <p className="text-secondary mt-2 line-clamp-2 flex-1 text-xs leading-5">
                        {p.description}
                      </p>

                      <div className="border-border mt-4 flex flex-wrap items-center justify-between gap-3 border-t pt-3">
                        <span className="border-border text-secondary bg-primary/5 rounded-full border px-2 py-0.5 text-[11px] font-semibold">
                          {p.category}
                        </span>

                        <span className="font-sora text-primary inline-flex items-center gap-1 text-[11px] font-semibold">
                          View details &rarr;
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </main>
      <Footer />

      {product.sizeGuide && (
        <SizeGuideModal
          isOpen={isSizeGuideOpen}
          sizeGuide={product.sizeGuide}
          onClose={() => setIsSizeGuideOpen(false)}
        />
      )}
    </>
  );
};
